import { ListItemIcon, ListItemText, PaperProps, SelectChangeEvent } from "@mui/material";
import CoreSelect, { CoreSelectProps } from ".";
import { CoreMenuItem } from "../menu";
import { CoreCheckbox } from "../checkbox/Checkbox";

export type CoreMultiSelectProps<T extends string> = Omit<
	CoreSelectProps<T[]>,
	"multiple" | "value" | "onChange" | "renderValue" | "label"
> & {
	/** map of option key → used to figure out what is selected */
	options: Record<T, boolean>;
	/** Rendered labels for each option */
	optionLabels?: Partial<Record<T, string>>;
	/** callback when selection changes */
	onChange: (event: SelectChangeEvent<T[]>) => void;
	/** Text to display when the select is closed */
	label: string;
	/** aria-label on the input */
	ariaLabel?: string;
	/** optional width of menu in ch units. Default is 12 */
	menuWidthCh?: number;
	/** optional filter to split/group keys however you like */
	filter?: (key: T) => boolean;
	/** height of each menu item (px). Default is 48 */
	itemHeight?: number;
	/** padding top before first menu item (px). Default is 8 */
	itemPaddingTop?: number;
	/** padding between top/bottom of menu (px). Default is 5 */
	menuVerticalPadding?: number;
	/** vertical gap between menu items (px). Default is 10 */
	menuItemGap?: number;
	/** left padding inside menu before checkmark (px). Default is 15 */
	leftPadding?: number;
	/** gap between checkmark and text (px). Default is 10 */
	checkmarkTextGap?: number;
	/** font size of each option's label (px or CSS size string). Defaults to MUI's body1 (16px) */
	itemFontSize?: number | string;
};

export default function CoreMultiSelect<T extends string>({
	options,
	optionLabels,
	onChange,
	label,
	ariaLabel,
	menuWidthCh = 12,
	filter,
	itemHeight = 48,
	itemPaddingTop = 8,
	menuVerticalPadding = 5,
	menuItemGap = 10,
	leftPadding = 15,
	checkmarkTextGap = 10,
	itemFontSize,
	...coreSelectProps
}: CoreMultiSelectProps<T>) {
	const selected = (Object.keys(options) as T[]).filter((key) => options[key]);
	return (
		<CoreSelect<T[]>
			multiple
			value={selected}
			onChange={onChange}
			label={label}
			inputProps={{ "aria-label": ariaLabel ?? label }}
			renderValue={() => label}
			displayEmpty
			MenuProps={{
				variant: "menu",
				anchorOrigin: { vertical: "bottom", horizontal: "left" },
				transformOrigin: { vertical: "top", horizontal: "left" },
				slotProps: {
					paper: {
						sx: {
							p: `${menuVerticalPadding}px 0px`,
							maxHeight: itemHeight * 4.5 + itemPaddingTop,
							width: `${menuWidthCh}ch`,
							boxShadow: "0px 0px 4px rgba(0,0,0,0.25)",
							// Mui being weird with paper props spreading
							...((coreSelectProps.MenuProps?.slotProps?.paper as PaperProps)?.sx ?? {}),
						},
					},
					...coreSelectProps.MenuProps?.slotProps,
				},
				...coreSelectProps.MenuProps,
			}}
			{...coreSelectProps}
		>
			{(Object.keys(options) as T[])
				.filter((key) => (filter ? filter(key) : true))
				.map((key, idx, arr) => (
					<CoreMenuItem
						key={key}
						value={key}
						sx={{
							height: itemHeight,
							mb: idx < arr.length - 1 ? `${menuItemGap}px` : 0,
							p: 0,
							pl: `${leftPadding}px`,
							display: "flex",
							alignItems: "center",
							"&.Mui-selected": {
								backgroundColor: "transparent",
								"&.Mui-focused, &.Mui-focusVisible": {
									backgroundColor: "transparent",
								},
								"&:hover": {
									backgroundColor: "#e7f1f3",
								},
							},
						}}
					>
						<ListItemIcon
							sx={{
								minWidth: "0 !important", // Override Mui inserting a min width
								mr: `${checkmarkTextGap}px`,
							}}
						>
							<CoreCheckbox
								checked={options[key]}
								inputProps={{ "aria-labelledby": `label-${key}` }}
								disableFocusOutline
							/>
						</ListItemIcon>
						<ListItemText
							id={`label-${key}`}
							primary={optionLabels?.[key] ?? key}
							primaryTypographyProps={itemFontSize !== undefined ? { fontSize: itemFontSize } : undefined}
						/>
					</CoreMenuItem>
				))}
		</CoreSelect>
	);
}
